import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CheckCircle2 } from 'lucide-react';

const CianoBites = () => {
  const offerings = [
    {
      title: 'Interactive Food Stations',
      desc: 'A high-impact setup designed to elevate the room—visually and emotionally. Ideal for receptions, celebrations, and team moments.',
    },
    {
      title: 'Branded Gifting & Boxed Drops',
      desc: 'Food gifts with brand presence—designed for delivery, gifting, and shareable moments.',
    },
    {
      title: 'Executive Meeting Drops',
      desc: 'Premium, polished catering built for high-visibility meetings and client hosting.',
    },
  ];

  const capabilities = [
    'Custom labels + packaging with your branding',
    'Insert cards / QR codes / messaging',
    'Delivery-ready presentation',
    'Corporate ordering available (single location or multi-drop)',
  ];

  const idealFor = [
    'Team celebrations and company gatherings',
    'Client appreciation + executive gifting',
    'Product launches + press drops',
    'Social events that want “wow” without a full production',
  ];

  const faqs = [
    { q: 'Do you handle dietary needs?', a: 'Yes—options available by menu.' },
    { q: 'Do you offer custom branding?', a: 'Yes—labels, inserts, and packaging standards are available.' },
    { q: 'Is this only for corporate?', a: 'No—social celebrations can book stations and gifting as well.' },
  ];

  return (
    <div className="bg-white pt-32 pb-20">
      {/* Hero */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-serif text-5xl md:text-7xl font-bold text-stone-900 mb-6">
            Ciano Bites
          </h1>
          <p className="text-2xl text-stone-600 mb-6">
            Interactive food stations + branded gifting—made for modern moments.
          </p>
          <p className="text-lg text-stone-600 mb-10 leading-relaxed">
            Designed in the U.S. for corporate teams, social celebrations, and brand-ready food experiences. From stations to boxed drops, everything is built to look intentional—and travel well.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-stone-900 text-white hover:bg-stone-800 px-8 py-6 text-base">
                Request a Station Menu
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-stone-900 text-stone-900 hover:bg-stone-900 hover:text-white px-8 py-6 text-base">
                Start a Branded Gifting Order
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="section-padding px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-8">What We Do</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <CheckCircle2 className="w-6 h-6 text-terracota flex-shrink-0 mt-1" />
              <p className="text-stone-600">Interactive stations for corporate and social events</p>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle2 className="w-6 h-6 text-terracota flex-shrink-0 mt-1" />
              <p className="text-stone-600">Branded gifting + boxed food items (client gifts, team drops, launches)</p>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle2 className="w-6 h-6 text-terracota flex-shrink-0 mt-1" />
              <p className="text-stone-600">Meeting moments (coffee breaks, executive snacks, curated bites)</p>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle2 className="w-6 h-6 text-terracota flex-shrink-0 mt-1" />
              <p className="text-stone-600">Seasonal collections for cultural and holiday moments</p>
            </div>
          </div>
        </div>
      </section>

      {/* Signature Offerings */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-8">Signature Offerings</h2>
          <div className="space-y-6">
            {offerings.map((item, idx) => (
              <Card key={idx} className="border-perla">
                <CardContent className="p-8">
                  <h3 className="font-serif text-2xl font-bold text-stone-900 mb-3">{item.title}</h3>
                  <p className="text-stone-600 leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Branded Gifting Capabilities */}
      <section className="section-padding px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-8">Branded Gifting Capabilities</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {capabilities.map((item, idx) => (
              <div key={idx} className="flex items-start space-x-3">
                <span className="w-2 h-2 bg-terracota rounded-full mt-2 flex-shrink-0"></span>
                <p className="text-stone-600">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-8">Ideal For</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {idealFor.map((item, idx) => (
              <div key={idx} className="flex items-start space-x-3">
                <span className="w-2 h-2 bg-terracota rounded-full mt-2 flex-shrink-0"></span>
                <p className="text-stone-600">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-12 text-center">How It Works</h2>
          <div className="space-y-6">
            {[
              { step: '1', title: 'Select station or gifting format', desc: 'Choose your service type' },
              { step: '2', title: 'Confirm headcount, branding, and delivery needs', desc: 'Define the details' },
              { step: '3', title: 'Menu + proposal', desc: 'Custom proposal and pricing' },
              { step: '4', title: 'Production + fulfillment', desc: 'We handle the execution' },
              { step: '5', title: 'Delivery / setup + feedback loop', desc: 'Flawless arrival' },
            ].map((item, idx) => (
              <div key={idx} className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-stone-900 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-serif text-xl font-bold text-stone-900 mb-1">{item.title}</h3>
                  <p className="text-stone-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-4xl font-bold text-stone-900 mb-12 text-center">FAQ</h2>
          <div className="space-y-8">
            {faqs.map((faq, idx) => (
              <div key={idx}>
                <h3 className="font-semibold text-lg text-stone-900 mb-2">{faq.q}</h3>
                <p className="text-stone-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="section-padding px-6 bg-stone-900 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6">
            Let's design a moment that looks premium—and travels well.
          </h2>
          <p className="text-lg mb-8 opacity-90">Response within 24–48 hours</p>
          <Link to="/contact">
            <Button className="bg-white text-stone-900 hover:bg-stone-100 px-12 py-6 text-lg font-semibold">
              Request a Station Menu
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CianoBites;